import type {
  AuditAction,
  ContentPageKind,
  ContentState,
  CustomerStatus,
  ErrorCode,
  FulfillmentMethod,
  NotShownReason,
  OrderStatus,
  PopupPlacement,
  ProductOrigin,
  ProductSourceState,
  ProductStatus,
  ShopperHiddenReason,
  PublishBlocker,
  ServiceTicketKind,
  ServiceTicketStatus,
  TenantRole,
  TenantStatus,
} from '@avrahami/contracts';
import type { NavGroup, NavKey } from '../lib/nav.js';

/** Server messages that deserve a precise translation (CONFLICT and friends). */
const serverMessages: Readonly<Record<string, string>> = {
  'Current password is incorrect': 'הסיסמה הנוכחית שגויה',
  'The code is not correct': 'הקוד שגוי',
  'This invitation link is invalid or has expired': 'קישור ההזמנה אינו תקף או שפג תוקפו',
  'Two-step verification is already on': 'אימות דו־שלבי כבר פעיל',
  'This person has already set a password and can sign in':
    'לאדם הזה כבר יש סיסמה, והוא יכול להיכנס',
  'Too many open service calls':
    'יש לכם יותר מדי קריאות פתוחות. המתינו לתשובה על הקיימות לפני שאתם פותחים חדשה.',
  'The active store changed in another tab':
    'החנות הפעילה הוחלפה בלשונית אחרת. הנתונים נטענים מחדש עבור החנות הנוכחית.',
};

/** The contracts' own validation messages (checked in the browser and by the API). */
const validation: Readonly<Record<string, string>> = {
  'the end must come after the start': 'הסיום חייב להיות אחרי ההתחלה',
  'duplicate product': 'מוצר מופיע פעמיים',
  'duplicate category': 'קטגוריה מופיעה פעמיים',
  'keep delivery or pickup on': 'יש להשאיר פעיל משלוח או איסוף עצמי',
  'a GA4 measurement ID looks like G-XXXXXXXXXX': 'מזהה GA4 נראה כך: G-XXXXXXXXXX',
  'a pixel ID is 6 to 20 digits': 'מזהה פיקסל הוא 6 עד 20 ספרות',
  '8 or 9 digits': '8 או 9 ספרות',
  'invalid phone number': 'מספר טלפון אינו תקין',
  'unknown time zone': 'אזור זמן לא מוכר',
  'a path on the store (starting with /) or an https:// address':
    'נתיב בחנות (מתחיל ב-/) או כתובת שמתחילה ב-https://',
  'the compare-at price must be higher than the price': 'המחיר לפני הנחה חייב להיות גבוה מהמחיר',
  'letters, digits and . _ / - only': 'אותיות לועזיות, ספרות ו-. _ / - בלבד',
  'nothing to change': 'אין מה לשנות',
  'an app password is 16 letters': 'סיסמת אפליקציה היא 16 אותיות באנגלית',
  REFUSED: 'Google לא קיבל את הכתובת והסיסמה. בדקו שהעתקתם את סיסמת האפליקציה במלואה.',
};

/**
 * Every string the tenant admin shows, in Hebrew. Components never hold literal text, so a second
 * language is a second dictionary (IMPLEMENTATION_PLAN Q12: Hebrew only for now).
 */
export const he = {
  app: { title: 'ניהול החנות', product: 'ניהול החנות' },
  nav: {
    dashboard: 'ראשי',
    orders: 'בקשות להצעת מחיר',
    customers: 'לקוחות',
    products: 'מוצרים',
    categories: 'קטגוריות',
    popups: 'חלונות קופצים',
    branding: 'לוגו',
    home: 'דף הבית',
    pages: 'עמודי תוכן',
    settings: 'הגדרות החנות',
    analytics: 'אנליטיקס',
    staff: 'צוות',
    activity: 'יומן פעולות',
    support: 'תמיכה ותקלות',
    account: 'החשבון שלי',
    logout: 'יציאה',
    menu: 'תפריט ניהול',
  } satisfies Record<NavKey | 'logout' | 'menu', string>,
  support: {
    title: 'תמיכה ותקלות',
    intro:
      'נתקלתם בתקלה, בבעיית אבטחה או בכל דבר שלא עובד כמו שצריך? פתחו כאן קריאה, וצוות הפלטפורמה יחזור אליכם בתשובה כאן בעמוד הזה.',
    newCall: 'פתיחת קריאה חדשה',
    kind: 'סוג הפנייה',
    kinds: { GENERAL: 'תקלה או שאלה', SECURITY: 'בעיית אבטחה' } satisfies Record<
      ServiceTicketKind,
      string
    >,
    securityHint:
      'למשל: מישהו נכנס לחשבון בלי רשותכם, מחירים או פרטים השתנו בלי שמישהו מכם שינה אותם, או קיבלתם מייל חשוד בשם החנות.',
    subject: 'נושא',
    subjectHint: 'במשפט אחד: מה לא עובד?',
    body: 'פירוט',
    bodyHint: 'מה ניסיתם לעשות, מה קרה, ובאיזה עמוד. כמה שיותר פרטים יעזרו לנו לטפל מהר.',
    send: 'שליחת הקריאה',
    sent: 'הקריאה נשלחה. נחזור אליכם כאן.',
    cancel: 'ביטול',
    list: 'הקריאות שלכם',
    empty: 'עוד לא פתחתם קריאות.',
    pick: 'בחרו קריאה מהרשימה כדי לראות את ההתכתבות.',
    notFound: 'הקריאה לא נמצאה.',
    tooMany: 'יש לכם יותר מדי קריאות פתוחות. המתינו לתשובה על הקיימות לפני שאתם פותחים חדשה.',
    opened: 'נפתחה',
    updated: 'עדכון אחרון',
    statuses: {
      OPEN: 'פתוחה',
      IN_PROGRESS: 'בטיפול',
      RESOLVED: 'טופלה',
    } satisfies Record<ServiceTicketStatus, string>,
    resolvedNote: 'הקריאה סומנה כמטופלת. אם הבעיה עדיין קיימת, כתבו כאן והיא תיפתח שוב.',
    messages: (count: number) => (count === 1 ? 'הודעה אחת' : `${String(count)} הודעות`),
    reply: {
      platform: 'צוות הפלטפורמה',
      store: 'אתם',
      empty: 'אין עדיין הודעות.',
      reply: 'הודעה לצוות הפלטפורמה',
      send: 'שליחה',
      sent: 'ההודעה נשלחה',
    },
  },
  navGroups: {
    requests: 'בקשות ולקוחות',
    catalog: 'קטלוג',
    store: 'החנות',
    team: 'צוות וחשבון',
  } satisfies Record<NavGroup, string>,
  common: {
    save: 'שמירה',
    saved: 'נשמר',
    create: 'יצירה',
    edit: 'עריכה',
    delete: 'מחיקה',
    remove: 'הסרה',
    cancel: 'ביטול',
    search: 'חיפוש',
    clear: 'ניקוי',
    all: 'הכול',
    none: '—',
    active: 'פעיל',
    inactive: 'כבוי',
    moveUp: 'הזזה למעלה',
    moveDown: 'הזזה למטה',
    invalidField: 'הערך אינו תקין',
    required: 'שדה חובה',
    taken: 'הערך כבר בשימוש',
    fixErrors: 'יש שדות שצריך לתקן',
    reload: 'טעינה מחדש',
    retry: 'נסו שוב',
    changedElsewhere:
      'מישהו אחר שינה את הנתונים האלה בינתיים. אפשר לטעון את הגרסה העדכנית (השינויים שלא נשמרו כאן יאבדו), או לשמור ולקבל הודעת התנגשות.',
    noPermission: 'לתפקיד שלך אין גישה למסך הזה.',
    readOnly: 'צפייה בלבד: לתפקיד שלך אין הרשאה לשנות כאן.',
    position: 'מיקום בסדר התצוגה',
    startsAt: 'תחילת תוקף',
    endsAt: 'סוף תוקף',
    windowHint: 'ריק = ללא הגבלה',
    status: 'מצב',
    actions: 'פעולות',
    deleteTitle: 'למחוק?',
    deleteBody: 'המחיקה סופית ואי אפשר לבטל אותה.',
  },
  money: {
    hint: 'בשקלים, כולל מע״מ (למשל 49.90)',
  },
  upload: {
    choose: 'בחירת תמונה',
    replace: 'החלפת התמונה',
    remove: 'הסרת התמונה',
    uploading: 'מעלה…',
    drop: 'גררו תמונה לכאן',
    dropMany: 'גררו תמונות לכאן',
    or: 'או',
    chooseFile: 'בחירת קובץ',
    close: 'סגירה',
    formats: 'JPEG,‏ PNG,‏ GIF,‏ WebP,‏ AVIF או SVG, עד 5MB לתמונה',
    tooMany: {
      one: 'אפשר להוסיף עוד תמונה אחת בלבד',
      many: 'אפשר להוסיף עוד {count} תמונות בלבד',
    },
    tooLarge: 'הקובץ גדול מ-5MB',
    notImage: 'אפשר להעלות רק תמונות (JPEG,‏ PNG,‏ GIF,‏ WebP,‏ AVIF,‏ SVG)',
    badSvg: 'לא הצלחנו לקרוא את קובץ ה-SVG. אפשר לייצא אותו שוב או להעלות PNG',
    preview: 'תצוגה מקדימה',
    none: 'אין תמונה',
  },
  login: {
    title: 'כניסה לניהול החנות',
    email: 'אימייל',
    password: 'סיסמה',
    submit: 'כניסה',
    codeTitle: 'אימות דו־שלבי',
    codeHint: 'הזינו את הקוד בן 6 הספרות מאפליקציית האימות, או אחד מקודי השחזור.',
    code: 'קוד אימות',
    verify: 'אימות',
    startOver: 'כניסה עם משתמש אחר',
    invalid: 'אימייל או סיסמה שגויים',
    wrongCode: 'הקוד שגוי. נסו שוב.',
  },
  devCode: {
    label: 'סביבת פיתוח, הקוד הנוכחי:',
    use: 'הזנת הקוד',
  },
  forgotPassword: {
    link: 'שכחתי סיסמה',
    title: 'איפוס סיסמה',
    hint: 'הזינו את האימייל שאיתו אתם נכנסים. נשלח אליו קישור לקביעת סיסמה חדשה.',
    email: 'אימייל',
    submit: 'שליחת קישור',
    sent: 'אם יש חשבון עם האימייל הזה, נשלח אליו קישור לקביעת סיסמה חדשה. הקישור תקף לחצי שעה. לא הגיע? בדקו גם בתיקיית הספאם.',
    notSetUp: 'שליחת מיילים עדיין לא הוגדרה במערכת. פנו למנהל הפלטפורמה.',
    goToLogin: 'חזרה למסך הכניסה',
  },
  passwordReset: {
    title: 'קביעת סיסמה חדשה',
    invalid: 'הקישור אינו תקף, כבר נוצל או שפג תוקפו. בקשו קישור חדש ב"שכחתי סיסמה".',
    missing: 'לא נמצא קישור. פתחו את הקישור המלא מהמייל.',
    forUser: 'סיסמה חדשה עבור',
    expires: 'הקישור בתוקף עד',
    password: 'סיסמה חדשה (לפחות 10 תווים)',
    confirm: 'אימות הסיסמה',
    mismatch: 'הסיסמאות אינן זהות',
    submit: 'שמירת הסיסמה',
    done: 'הסיסמה החדשה נשמרה, וכל החיבורים הקודמים לחשבון נותקו. אפשר להיכנס עכשיו.',
    goToLogin: 'למסך הכניסה',
  },
  invitation: {
    title: 'הגדרת סיסמה',
    invalid: 'קישור ההזמנה אינו תקף או שפג תוקפו. בקשו קישור חדש ממי ששלח לכם אותו.',
    missing: 'לא נמצא קישור הזמנה. פתחו את הקישור המלא שקיבלתם.',
    forUser: 'הגדרת סיסמה עבור',
    expires: 'בתוקף עד',
    password: 'סיסמה חדשה (לפחות 10 תווים)',
    confirm: 'אימות הסיסמה',
    mismatch: 'הסיסמאות אינן זהות',
    submit: 'שמירת הסיסמה',
    done: 'הסיסמה נשמרה. אפשר להיכנס עכשיו.',
    goToLogin: 'למסך הכניסה',
  },
  noStore: {
    title: 'אין חנות פעילה',
    body: 'החשבון מחובר, אבל אינו חבר בצוות של אף חנות. אם הוזמנתם לחנות, בקשו מבעל החנות קישור הזמנה חדש.',
    choose: 'בחרו חנות לניהול:',
    enter: 'כניסה לחנות',
  },
  enter: {
    title: 'כניסה לניהול החנות',
    expired:
      'הקישור כבר אינו בתוקף. חזרו לניהול הפלטפורמה ולחצו שוב על "כניסה לניהול החנות" בעמוד החנות.',
    toLogin: 'לכניסה הרגילה לניהול החנות',
  },
  managed: {
    banner:
      'אתם מנהלים את החנות הזו מטעם צוות הפלטפורמה, עם כל ההרשאות של בעל החנות. כל פעולה נרשמת ביומן החנות על שמכם.',
  },
  switcher: {
    label: 'החנות שבניהול',
    /** Shown next to the list when the member has more than one store. */
    visible: (count: number) => `החלפת חנות (${String(count)})`,
  },
  suspended: {
    banner:
      'החנות מושעית: היא אינה זמינה לקונים, ואפשר רק לצפות בנתונים. לפרטים פנו לצוות הפלטפורמה.',
  },
  dashboard: {
    title: 'ראשי',
    storeLink: 'לחנות',
    noUrl: 'לחנות עדיין אין כתובת פעילה',
    notOpened: 'החנות עדיין לא נפתחה לקונים.',
    siteClosed: 'החנות סגורה זמנית לקונים (לפי ההגדרות).',
    openSettings: 'להגדרות',
    orders: 'בקשות להצעת מחיר',
    newOrders: 'חדשות',
    inProgress: 'בטיפול',
    today: 'התקבלו היום',
    ordersLink: 'לכל הבקשות להצעת מחיר',
    products: 'מוצרים',
    activeProducts: 'מוצגים בחנות',
    draftProducts: 'טיוטות',
    hiddenProducts: 'מוסתרים',
    withoutCopy: 'ממתינים לתוכן',
    sourceDiscontinued: 'הופסקו במקור',
    customers: 'לקוחות רשומים',
    notShownOne: 'מוצר אחד בחנות שלכם אינו מוצג לקונים',
    notShownMany: '{count} מוצרים בחנות שלכם אינם מוצגים לקונים',
    notShownWhy: 'הסיבה לכל קבוצה, ומה לעשות:',
    notShownLink: 'לרשימה',
    copyNote:
      'התוכן השיווקי של המוצרים נכתב ומנוהל על ידי צוות הפלטפורמה. אפשר לערוך את הטקסטים של מוצר ידנית בעמוד המוצר.',
  },
  // Quote requests (adr/0022): the keys keep the name of the API's resource.
  orders: {
    title: 'בקשות להצעת מחיר',
    filters: 'סינון',
    status: 'סטטוס',
    search: 'חיפוש (מספר בקשה, שם, אימייל או טלפון)',
    from: 'מתאריך',
    to: 'עד תאריך',
    apply: 'סינון',
    empty: 'אין בקשות להצגה',
    number: 'מס׳',
    date: 'תאריך',
    customer: 'לקוח',
    total: 'שווי לפי מחירון',
    items: 'יחידות',
    back: 'לרשימת הבקשות',
    orderTitle: 'בקשה להצעת מחיר מס׳',
    placedAt: 'התקבלה',
    contact: 'פרטי הלקוח',
    name: 'שם',
    email: 'אימייל',
    phone: 'טלפון',
    toCustomer: 'לכרטיס הלקוח',
    guest: 'בקשה ללא חשבון',
    delivery: 'קבלת הסחורה',
    fulfillment: 'אופן קבלה',
    address: 'כתובת',
    apartment: 'דירה',
    zip: 'מיקוד',
    notes: 'הערות הלקוח',
    noNotes: 'הלקוח לא כתב הערות',
    lines: 'המוצרים המבוקשים',
    product: 'מוצר',
    sku: 'מק״ט',
    quantity: 'כמות',
    unitPrice: 'מחיר מחירון',
    lineTotal: 'סה״כ',
    grandTotal: 'סה״כ לפי מחירון',
    vat: 'מתוכו מע״מ',
    listPriceNote:
      'המחירים כאן הם מחירי המחירון של החנות ביום הבקשה (הלקוח רואה אותם רק אם הצגת המחירים פעילה בהגדרות החנות). את ההצעה ללקוח עורכים למטה, ב״הצעת מחיר ללקוח״.',
    history: 'היסטוריה',
    changedBy: 'שונה על ידי',
    byShopper: 'הלקוח',
    changeStatus: 'שינוי סטטוס',
    newStatus: 'סטטוס חדש',
    applyStatus: 'עדכון הסטטוס',
    finalStatus: 'הבקשה בוטלה; אי אפשר לשנות את הסטטוס שלה.',
    cancelTitle: 'לבטל את הבקשה?',
    cancelBody: 'ביטול הוא סופי: אי אפשר להחזיר בקשה שבוטלה.',
    cancelConfirm: 'ביטול הבקשה',
    document: 'הורדת הבקשה (PDF)',
    updated: 'הבקשה עודכנה',
  },
  quote: {
    title: 'הצעת מחיר ללקוח',
    intro:
      'כאן עורכים את ההצעה שנשלחת ללקוח: מחירים, כמויות, מוצרים ושורות נוספות. הבקשה המקורית של הלקוח לא משתנה.',
    draft: 'טיוטה: עדיין לא נשמרה',
    savedAt: 'נשמרה',
    savedBy: 'על ידי',
    product: 'מוצר / תיאור',
    sku: 'מק״ט',
    quantity: 'כמות',
    unitPrice: 'מחיר ליח׳ (כולל מע״מ)',
    lineTotal: 'סה״כ',
    titlePlaceholder: 'למשל: הובלה והתקנה',
    removeLine: 'הסרת השורה',
    addProduct: 'הוספת מוצר מהחנות',
    addCustom: 'הוספת שורה חופשית',
    searchProducts: 'חיפוש מוצר (שם או מק״ט)',
    searching: 'מחפש…',
    noProducts: 'לא נמצאו מוצרים',
    noPrice: 'ללא מחיר',
    closeSearch: 'סגירת החיפוש',
    beforeVat: 'סה״כ לפני מע״מ',
    vat: 'מע״מ',
    total: 'סה״כ לתשלום',
    validUntil: 'בתוקף עד',
    paymentTerms: 'תנאי תשלום',
    paymentTermsPlaceholder: 'למשל: שוטף + 30, העברה בנקאית',
    notes: 'הערות ללקוח',
    notesHint: 'יופיעו בתחתית ההצעה',
    save: 'שמירת ההצעה',
    discard: 'ביטול השינויים',
    saved: 'ההצעה נשמרה',
    download: 'הורדת הצעת המחיר (PDF)',
    saveFirst: 'יש לשמור את השינויים לפני ההורדה',
    readOnly: 'צפייה בלבד: לתפקיד שלך אין הרשאה לערוך את ההצעה.',
    noLines: 'צריך לפחות שורה אחת בהצעה',
    tooManyLines: 'יותר מדי שורות בהצעה',
    badQuantity: 'כמות: מספר שלם בין 1 ל־10,000',
    badPrice: 'מחיר בשקלים, למשל 1250 או 1250.50',
    needsTitle: 'צריך לכתוב תיאור לשורה',
  },
  orderStatus: {
    NEW: 'חדשה',
    IN_PROGRESS: 'בטיפול',
    QUOTED: 'נשלחה הצעה',
    CLOSED: 'נסגרה',
    CANCELLED: 'בוטלה',
  } satisfies Record<OrderStatus, string>,
  fulfillment: {
    DELIVERY: 'משלוח',
    PICKUP: 'איסוף עצמי',
  } satisfies Record<FulfillmentMethod, string>,
  customers: {
    title: 'לקוחות',
    search: 'חיפוש (שם, אימייל או טלפון)',
    status: 'מצב חשבון',
    empty: 'אין לקוחות להצגה',
    name: 'שם',
    email: 'אימייל',
    phone: 'טלפון',
    orders: 'בקשות',
    lastOrder: 'בקשה אחרונה',
    joined: 'הצטרפות',
    lastLogin: 'כניסה אחרונה',
    back: 'לרשימת הלקוחות',
    details: 'פרטים',
    customerOrders: 'בקשות להצעת מחיר של הלקוח',
    noOrders: 'אין בקשות',
    disable: 'השבתת החשבון',
    enable: 'הפעלת החשבון',
    disableTitle: 'להשבית את החשבון?',
    disableBody:
      'הלקוח ינותק מכל המכשירים ולא יוכל להיכנס. הבקשות שלו נשמרות. אפשר להפעיל את החשבון מחדש בכל עת.',
    enableTitle: 'להפעיל את החשבון מחדש?',
    enableBody: 'הלקוח יוכל להיכנס שוב לחשבון שלו בחנות.',
  },
  customerStatus: {
    ACTIVE: 'פעיל',
    DISABLED: 'מושבת',
  } satisfies Record<CustomerStatus, string>,
  products: {
    title: 'מוצרים',
    create: 'מוצר חדש',
    pull: 'משיכת מוצרים חדשים',
    search: 'חיפוש (שם, מק״ט, דגם או ברקוד)',
    status: 'מצב',
    origin: 'מקור',
    category: 'קטגוריה',
    sourceState: 'מצב בקטלוג',
    notShown: 'לא מוצגים בחנות, כי',
    empty: 'אין מוצרים להצגה',
    name: 'מוצר',
    sku: 'מק״ט',
    cost: 'מחיר עלות',
    price: 'מחיר מכירה',
    profit: 'רווח',
    edit: 'עריכת המוצר',
    update: 'עדכון',
    updated: 'עודכן',
    reloadRow: 'טעינה מחדש',
    zeroPriceHint: 'מוצג בחנות בלי מחיר',
    lockedFromCatalog: 'מגיע מהקטלוג ואינו ניתן לשינוי',
    back: 'לרשימת המוצרים',
    newTitle: 'מוצר חדש',
    facts: 'פרטים טכניים',
    factsLocked: 'הפרטים הטכניים מגיעים מהקטלוג ואינם ניתנים לשינוי. אפשר להוסיף שורות מפרט משלכם.',
    factName: 'שם המוצר',
    factSku: 'מק״ט',
    barcode: 'ברקוד',
    model: 'דגם',
    manufacturer: 'יצרן',
    specs: 'מפרט',
    extraSpecs: 'שורות מפרט נוספות',
    specLabel: 'תכונה',
    specValue: 'ערך',
    addSpec: 'הוספת שורה',
    removeSpec: 'הסרת השורה',
    content: 'תוכן שיווקי',
    contentState: 'מצב התוכן',
    copyNote:
      'התוכן השיווקי נכתב ומנוהל על ידי צוות הפלטפורמה. אפשר לערוך כאן את הטקסטים של המוצר ידנית.',
    titleField: 'כותרת בחנות',
    description: 'תיאור',
    seoTitle: 'כותרת לגוגל (SEO)',
    seoDescription: 'תיאור לגוגל (SEO)',
    pricing: 'מחיר והצגה בחנות',
    costField: 'מחיר עלות (₪)',
    costLockedHint:
      'המחיר שלכם באתר המקורי (לפי המחירון שלכם שם, או מחיר מיוחד שנקבע לכם), כולל מע״מ. מתעדכן אוטומטית.',
    costHint: 'כמה המוצר עולה לכם, כולל מע״מ. ריק = לא ידוע',
    priceField: 'מחיר מכירה (₪)',
    priceHint: 'בשקלים, כולל מע״מ. ברירת המחדל: 100% רווח על מחיר העלות',
    profitField: 'רווח',
    profitHint: 'מחיר המכירה פחות מחיר העלות',
    compareAt: 'מחיר לפני הנחה (₪)',
    compareAtHint: 'ריק = אין',
    categories: 'תת-קטגוריות',
    categoriesHint: 'מוצר משויך לתת-קטגוריה, לא לקטגוריה ראשית.',
    noCategories: 'אין עדיין תת-קטגוריות. יוצרים אותן במסך "קטגוריות".',
    mainCategory: 'קטגוריה ראשית: מומלץ להעביר לתת-קטגוריה',
    statusField: 'מצב בחנות',
    blockers: 'המוצר עדיין לא יכול להיות מוצג בחנות:',
    cannotShow: 'אי אפשר להציג את המוצר בחנות, כי חסר בו:',
    blockersNext: 'אחרי שתשלימו את החסר, בחרו "מוצג בחנות" בשדה "מצב בחנות" ושמרו.',
    pricesInList: 'את מחיר המכירה עורכים ישירות ברשימת המוצרים.',
    notShownReady:
      'לא חסר במוצר דבר, אבל מצבו "{status}" ולכן הקונים לא רואים אותו. כדי להציג אותו בחרו "מוצג בחנות" בשדה "מצב בחנות" ושמרו.',
    costMissingHint: 'באתר המקורי לא נקבע לכם מחיר למוצר הזה. כשייקבע שם מחיר, הוא יופיע כאן.',
    sourceDiscontinued:
      'המוצר הופסק בקטלוג. בחנות שלכם לא השתנה דבר: אפשר להמשיך להציע אותו, להסתיר אותו או להעביר אותו לארכיון.',
    gallery: 'תמונות',
    galleryEmpty: 'אין תמונות',
    altText: 'טקסט חלופי',
    addImage: 'הוספת תמונה',
    saveGallery: 'שמירת התמונות',
    galleryFull: 'הגעתם למספר התמונות המרבי',
    image: 'תמונה',
    logo: {
      title: 'לוגו יצרן',
      alt: 'לוגו היצרן',
      CATALOG: 'הלוגו מהקטלוג, כמו באתר המקורי. הוא מתעדכן יחד עם הקטלוג.',
      catalogNone: 'באתר המקורי אין לוגו יצרן למוצר הזה. אפשר להעלות לוגו משלכם.',
      UPLOAD: 'לוגו שהעליתם. הקטלוג לא ישנה אותו.',
      NONE: 'בחרתם לא להציג לוגו יצרן במוצר הזה.',
      none: 'אין לוגו',
      upload: 'העלאת לוגו',
      replace: 'העלאת לוגו אחר',
      remove: 'הסרת הלוגו',
      backToCatalog: 'חזרה ללוגו מהקטלוג',
      saved: 'הלוגו נשמר',
    },
  },
  productStatus: {
    DRAFT: 'טיוטה',
    ACTIVE: 'מוצג בחנות',
    HIDDEN: 'לא מוצג בחנות',
    ARCHIVED: 'בארכיון',
  } satisfies Record<ProductStatus, string>,
  /** Why shoppers do not see an ACTIVE product (adr/0046). */
  shopperHidden: {
    NOT_ON_SITE: 'לא מוצג: לא מוצג לכם באתר המקורי',
    CATEGORY_HIDDEN: 'לא מוצג: הקטגוריה מוסתרת',
  } satisfies Record<ShopperHiddenReason, string>,
  shopperHiddenHelp: {
    NOT_ON_SITE:
      'המוצר במצב "מוצג בחנות", אבל באתר המקורי הוא לא מוצג כרגע ללקוח שלכם (למשל: אזל מהמלאי, או שלא נקבע לכם בו מחיר). החנות מציגה בדיוק את מה שמוצג לכם שם, והמוצר יחזור אליה מעצמו כשיוצג שם שוב.',
    CATEGORY_HIDDEN:
      'המוצר במצב "מוצג בחנות", אבל כל הקטגוריות שלו מוסתרות (הן עצמן או קטגוריית האב שלהן), ולכן הקונים לא רואים אותו: לא בחיפוש ולא בקישור ישיר. כדי להציג אותו, הציגו את הקטגוריה במסך "קטגוריות" או שייכו את המוצר לתת-קטגוריה מוצגת.',
  } satisfies Record<ShopperHiddenReason, string>,
  productOrigin: {
    MASTER_CATALOG: 'מהקטלוג',
    TENANT_CUSTOM: 'מוצר שלי',
  } satisfies Record<ProductOrigin, string>,
  productSourceState: {
    IN_SYNC: 'פעיל בקטלוג',
    SOURCE_DISCONTINUED: 'הופסק בקטלוג',
  } satisfies Record<ProductSourceState, string>,
  productPull: {
    title: 'משיכת מוצרים חדשים',
    summary: 'מה חדש בקטלוג',
    candidates: 'מוצרים בקטלוג שעדיין אין בחנות',
    newSinceLastPull: 'נוספו לקטלוג מאז המשיכה האחרונה',
    lastPulledAt: 'המשיכה האחרונה',
    neverPulled: 'עדיין לא בוצעה',
    automatic:
      'מוצרים חדשים שנוספים לקטלוג נכנסים לחנות אוטומטית, בעדכון הקטלוג היומי, בלי שצריך למשוך אותם. כאן אפשר להוסיף מוצרים ותיקים יותר מהקטלוג שעדיין אין בחנות.',
    principle:
      'המשיכה רק מוסיפה מוצרים, ואינה משנה מוצרים שכבר בחנות. מוצרים חדשים נכנסים כטיוטות, ומוסתרים מהקונים עד שיש להם תוכן שיווקי משלכם.',
    runActiveAutomatic: 'מוצרים חדשים מהקטלוג נכנסים לחנות',
    runLastAutomatic: 'מוצרים חדשים שנכנסו אוטומטית',
    discontinuedOne: 'מוצר אחד בחנות הופסק בקטלוג.',
    discontinuedMany: '{count} מוצרים בחנות הופסקו בקטלוג.',
    discontinuedHint: 'שום דבר לא הוסר: אתם מחליטים אם להמשיך להציע, להסתיר או להסיר.',
    discontinuedLink: 'למוצרים שהופסקו',
    removedOne: 'מוצר אחד מהקטלוג שהסרתם מהחנות אינו מוצע שוב.',
    removedMany: '{count} מוצרים מהקטלוג שהסרתם מהחנות אינם מוצעים שוב.',
    removedLink: 'לשחזור מרשימת המוצרים',
    runActive: 'משיכה מתבצעת',
    runLast: 'המשיכה האחרונה',
    runProgress: 'מוסיף מוצרים… {added} מתוך {requested}',
    runAddedNone: 'לא נוספו מוצרים',
    runAddedOne: 'נוסף מוצר אחד',
    runAddedMany: 'נוספו {count} מוצרים',
    runCategoriesOne: 'וקטגוריה אחת',
    runCategoriesMany: 'ו-{count} קטגוריות',
    runFinishedAt: 'הסתיימה ב-{when}',
    runDraftsLink: 'לטיוטות ברשימת המוצרים',
    runFailed: 'המשיכה נכשלה. מה שכבר נוסף נשאר בחנות ({added} מתוך {requested}), ואפשר למשוך שוב.',
    list: 'מוצרים שאפשר להוסיף',
    search: 'חיפוש (שם, מק״ט, דגם או ברקוד)',
    onlyNew: 'רק מוצרים שנוספו מאז המשיכה האחרונה',
    empty: 'החנות מעודכנת: אין מוצרים חדשים בקטלוג',
    noResults: 'לא נמצאו מוצרים מתאימים',
    selectAll: 'בחירת כל המוצרים שברשימה',
    select: 'בחירת המוצר',
    image: 'תמונה',
    noImage: 'אין תמונה',
    name: 'מוצר',
    newBadge: 'חדש',
    sku: 'מק״ט',
    categories: 'קטגוריות',
    cost: 'מחיר עלות',
    startPrice: 'מחיר מכירה התחלתי',
    noPrice: 'ללא מחיר',
    addedAt: 'נוסף לקטלוג',
    addSelected: 'הוספת המוצרים שנבחרו ({count})',
    addAll: 'הוספת כל המוצרים ({count})',
    clearSelection: 'ניקוי הבחירה',
    overLimit:
      'אפשר להוסיף עד {max} מוצרים נבחרים בכל פעם. להוספת הכול השתמשו ב"הוספת כל המוצרים".',
    confirmTitle: 'להוסיף את כל המוצרים?',
    confirmOne: 'מוצר אחד יתווסף לחנות כטיוטה. מוצרים שכבר בחנות לא ישתנו.',
    confirmMany: '{count} מוצרים יתווספו לחנות כטיוטות. מוצרים שכבר בחנות לא ישתנו.',
    confirm: 'הוספה',
    conflict: 'משיכה אחרת כבר מתבצעת, או שאין מוצרים חדשים להוסיף.',
    unavailable: 'חלק מהמוצרים שנבחרו כבר אינם זמינים בקטלוג. הרשימה עודכנה; בחרו שוב.',
  },
  /** The strengths, "ideal for" lines and care instructions of a product (adr/0055). */
  productLists: {
    title: 'יתרונות, מתאים במיוחד ל, תחזוקה וניקיון',
    note: 'מה שתשנו כאן נשאר שלכם: עדכוני הקטלוג לא ידרסו אותו. רשימה שלא שיניתם ממשיכה להתעדכן אוטומטית מהקטלוג. כדי להחזיר רשימה שערכתם לעדכון האוטומטי, פנו לצוות הפלטפורמה.',
    names: {
      features: 'יתרונות',
      idealFor: 'מתאים במיוחד ל...',
      care: 'הוראות תחזוקה וניקיון',
    },
    states: {
      EDITED: 'נערך ידנית',
      AUTO: 'נכתב אוטומטית',
      PENDING: 'ייכתב אוטומטית',
      WRITING: 'נכתב עכשיו מהקטלוג…',
    },
    empty: 'אין שורות. רשימה ריקה לא מוצגת בעמוד המוצר.',
    limit: 'עד {count} שורות',
    featureIcon: 'סמל',
    featureTitle: 'כותרת',
    featureText: 'משפט הסבר',
    idealForLine: 'סוג עסק',
    careLine: 'הוראה',
    addLine: 'הוספת שורה',
    removeLine: 'הסרה',
    icons: {
      SPEED: 'מהירות',
      CLEAN: 'ניקיון והיגיינה',
      SHIELD: 'עמידות ובטיחות',
      SAVINGS: 'חיסכון',
      QUALITY: 'איכות',
      CAPACITY: 'קיבולת',
      VARIETY: 'מגוון',
      POWER: 'עוצמה',
      COLD: 'קירור',
      HEAT: 'חימום ובישול',
      PRECISION: 'דיוק ושליטה',
      EASY: 'קלות הפעלה',
      SPACE: 'חיסכון במקום',
      SERVICE: 'שירות ותחזוקה',
    },
  },
  contentState: {
    SOURCE_PENDING: 'ממתין לתוכן',
    AI_GENERATED: 'נכתב אוטומטית',
    APPROVED: 'אושר',
    TENANT_EDITED: 'נערך בחנות',
  } satisfies Record<ContentState, string>,
  flyer: {
    title: 'פלייר שיווקי',
    intro:
      'תמונה מרובעת של המוצר לשיתוף ברשתות החברתיות ובוואטסאפ, בעיצוב של התבנית של החנות, בצבעים ובלוגו שלה. הכותרת, התיאור, המפרט והמחיר נלקחים מעמוד המוצר בחנות בכל פעם שהפלייר מוצג.',
    download: 'הורדת הפלייר (PNG)',
    drawing: 'מכין את הפלייר…',
    imageLabel: 'פלייר שיווקי: {title}',
    downloadFailed: 'לא הצלחנו לשמור את התמונה. נסו שוב.',
    noPrice: 'המחיר מופיע בפלייר רק כשהחנות מציגה מחירים ולמוצר יש מחיר.',
  },
  publishBlockers: {
    ARCHIVED: 'המוצר בארכיון',
    NO_COPY: 'אין תוכן שיווקי משלכם',
  } satisfies Record<PublishBlocker, string>,
  /** What to do about each blocker, on the product's own screen. */
  publishBlockerHelp: {
    ARCHIVED: 'כדי להחזיר אותו לחנות בחרו מצב אחר בשדה "מצב בחנות".',
    NO_COPY:
      'התוכן נכתב על ידי צוות הפלטפורמה ויופיע כאן כשיהיה מוכן. אפשר גם לכתוב כותרת ותיאור כאן בעצמכם.',
  } satisfies Record<PublishBlocker, string>,
  /** Why a product the store holds is not shown to shoppers. */
  notShownReason: {
    NO_COPY: 'ממתין לתוכן שיווקי',
    READY: 'מוכן, ממתין שתציגו אותו',
  } satisfies Record<NotShownReason, string>,
  notShownAction: {
    NO_COPY: 'התוכן השיווקי שלהם עדיין לא נכתב. צוות הפלטפורמה כותב אותו.',
    READY: 'לא חסר בהם דבר. בחרו "מוצג בחנות" בעמודת "מצב" ברשימת המוצרים כדי להציג אותם.',
  } satisfies Record<NotShownReason, string>,
  categories: {
    title: 'קטגוריות',
    create: 'קטגוריה חדשה',
    name: 'שם הקטגוריה',
    parent: 'קטגוריית אב',
    topLevel: 'ראשית (ללא אב)',
    position: 'מיקום',
    visible: 'מוצגת בחנות',
    hidden: 'מוסתרת',
    origin: 'מקור',
    subCount: 'תת-קטגוריות',
    intro: 'קטגוריה ראשית נפתחת אל תת-הקטגוריות שלה. מוצרים משויכים לתת-קטגוריות בלבד.',
    empty: 'אין קטגוריות',
    notEmpty: 'אי אפשר למחוק: בקטגוריה יש מוצרים או קטגוריות משנה.',
    back: 'לרשימת הקטגוריות',
    missing: 'הקטגוריה לא נמצאה. ייתכן שנמחקה.',
    image: 'תמונת הקטגוריה',
    imageHint: 'מוצגת בחנות על כרטיס הקטגוריה. בלי תמונה החנות מציגה את תמונת הקטגוריה מהקטלוג.',
    status: 'מצב בחנות',
    addImage: 'הוספת תמונה לקטגוריה {name}',
    replaceImage: 'החלפת התמונה של {name}',
    removeImage: 'הסרת התמונה של {name}',
    dropHere: 'שחררו כאן',
    changedMeanwhile: 'הקטגוריה שונתה בינתיים במקום אחר. מוצג עכשיו המצב העדכני, נסו שוב.',
    tableHelp:
      'גררו תמונה אל המשבצת שליד הקטגוריה או לחצו עליה, ובחרו מצב מהרשימה. השינוי נשמר מיד.',
  },
  popups: {
    title: 'חלונות קופצים',
    create: 'חלון חדש',
    editTitle: 'עריכת חלון קופץ',
    newTitle: 'חלון קופץ חדש',
    intro: 'חלון קופץ מוצג לכל קונה פעם אחת (עד שהחלון משתנה).',
    titleField: 'כותרת',
    body: 'טקסט',
    image: 'תמונה',
    buttonLabel: 'טקסט הכפתור',
    link: 'קישור הכפתור',
    linkHint: 'נתיב בחנות (למשל /c/...) או כתובת https://',
    placement: 'איפה להציג',
    isActive: 'החלון פעיל',
    window: 'תוקף',
    empty: 'אין חלונות קופצים',
  },
  placement: {
    ALL_PAGES: 'בכל העמודים',
    HOME: 'בדף הבית בלבד',
  } satisfies Record<PopupPlacement, string>,
  branding: {
    title: 'לוגו',
    logo: 'לוגו',
    favicon: 'סמל לשונית (favicon)',
    faviconHint: 'תמונה ריבועית קטנה, למשל 64×64',
  },
  home: {
    title: 'דף הבית',
    hero: 'באנר הפתיחה',
    heroEyebrow: 'שורה מעל הכותרת',
    heroTitle: 'כותרת',
    heroHighlight: 'שורה שנייה של הכותרת (בצבע המותג)',
    heroText: 'טקסט',
    heroButtonLabel: 'טקסט הכפתור',
    heroButtonLink: 'קישור הכפתור',
    heroHint:
      'שדה ריק מציג את ברירת המחדל של החנות. את המילים הראשונות כותב ה-AI לחנות, ואפשר לשנות אותן כאן.',
    showNewest: 'הצגת שורת "מוצרים חדשים"',
    featured: 'קטגוריות מוצגות',
    featuredHint: 'עד {max} קטגוריות, לפי הסדר. בלי בחירה מוצגות הקטגוריות הראשיות.',
    addCategory: 'הוספת קטגוריה',
    chooseCategory: 'בחירת קטגוריה',
    noFeatured: 'לא נבחרו קטגוריות',
    missingCategory: 'קטגוריה שנמחקה (תוסר בשמירה)',
    featuredFull: 'הגעתם למספר הקטגוריות המרבי',
    slides: 'באנרים מתחלפים',
    slidesHint:
      'עד {max} באנרים. גודל תמונה מומלץ: 1600×600 פיקסלים. התמונה מוצגת תמיד בשלמותה, בלי חיתוך: באנר רחב ממלא את המסגרת, ותמונה צרה יותר (למשל תמונת מוצר) מוצגת בצד, והכותרת לצידה על הצבע הראשי של החנות.',
    createSlide: 'באנר חדש',
    editSlide: 'עריכת באנר',
    newSlide: 'באנר חדש',
    slideImage: 'תמונת הבאנר',
    slideTitle: 'כותרת',
    slideText: 'טקסט',
    slideButton: 'טקסט הכפתור',
    slideLink: 'קישור',
    slideActive: 'הבאנר פעיל',
    noSlides: 'אין באנרים',
    slidesFull: 'הגעתם למספר הבאנרים המרבי',
  },
  pages: {
    title: 'עמודי תוכן',
    create: 'עמוד חדש',
    createKind: 'יצירת עמוד "{kind}"',
    missing: 'עמודים שעדיין אין לחנות:',
    empty: 'אין עמודי תוכן',
    back: 'לרשימת העמודים',
    newTitle: 'עמוד תוכן חדש',
    kind: 'סוג העמוד',
    titleField: 'כותרת העמוד',
    slug: 'כתובת העמוד',
    slugHint: 'אותיות לועזיות קטנות, ספרות ומקפים',
    body: 'תוכן העמוד',
    bodyHint: 'טקסט פשוט; שורה ריקה מפרידה בין פסקאות.',
    published: 'העמוד מפורסם',
    draft: 'טיוטה',
    updated: 'עודכן',
    deleteTitle: 'למחוק את העמוד?',
    full: 'הגעתם למספר עמודי התוכן המרבי.',
    kinds: {
      ABOUT: 'אודות',
      CONTACT: 'צור קשר',
      FAQ: 'שאלות נפוצות',
      TERMS: 'תקנון',
      PRIVACY: 'מדיניות פרטיות',
      ACCESSIBILITY: 'הצהרת נגישות',
      CUSTOM: 'עמוד אחר',
    } satisfies Record<ContentPageKind, string>,
  },
  settings: {
    title: 'הגדרות החנות',
    store: 'החנות ובקשות להצעת מחיר',
    siteOpen: 'האתר פתוח לקונים',
    siteOpenHint: 'כבוי: מוצג עמוד "סגור זמנית" ללא פרטי החנות',
    ordersEnabled: 'קבלת בקשות להצעת מחיר',
    ordersEnabledHint: 'כבוי: הקטלוג זמין לצפייה, בלי אפשרות לשלוח בקשה',
    guestCheckout: 'שליחת בקשה ללא הרשמה',
    guestCheckoutHint: 'כבוי: רק לקוחות רשומים יכולים לשלוח בקשה',
    registration: 'הרשמת לקוחות לאתר',
    showPrices: 'הצגת מחירים לקונים',
    showPricesHint:
      'כבוי: הקונים לא רואים מחירים, והמחיר נמסר בהצעת המחיר. פעיל: מחיר המחירון מוצג ליד כל מוצר.',
    business: 'פרטי העסק',
    businessHint:
      'הפרטים האלה מוצגים לקונים באתר החנות, באזור "יצירת קשר" שבתחתית כל עמוד. שדה ריק לא מוצג.',
    legalName: 'שם העסק הרשמי',
    legalNameHint: 'מופיע במסמכים; ריק = שם החנות',
    businessId: 'ח.פ. / ע.מ.',
    phone: 'טלפון',
    phoneHint: 'כפתור החיוג באתר החנות מחייג למספר הזה',
    whatsapp: 'מספר וואטסאפ',
    whatsappHint:
      'אליו נשלחות בקשות הצעת מחיר מכפתורי הוואטסאפ באתר. ריק = מספר הטלפון. בלי מספר הכפתורים לא מוצגים.',
    email: 'אימייל לפניות',
    address: 'כתובת העסק',
    website: 'אתר נוסף',
    seo: 'קידום בגוגל (SEO)',
    seoHint: 'הטקסטים נכתבים אוטומטית על ידי המערכת, ואפשר לערוך אותם כאן.',
    seoTitle: 'כותרת דף הבית',
    seoDescription: 'תיאור דף הבית',
  },
  mail: {
    title: 'קבלת בקשות להצעת מחיר במייל',
    intro:
      'חברו את חשבון ה-Gmail של החנות, וכל בקשה חדשה להצעת מחיר תישלח אליו מיד. תשובה למייל נשלחת ישירות ללקוח.',
    none: 'לא חובר חשבון Gmail.',
    step1: 'הפעילו אימות דו־שלבי בחשבון Google של החנות (חובה כדי ליצור סיסמת אפליקציה).',
    step2: 'צרו "סיסמת אפליקציה" בכתובת',
    step3: 'הדביקו כאן את 16 האותיות ש-Google מציג, יחד עם כתובת ה-Gmail.',
    address: 'כתובת ה-Gmail',
    appPassword: 'סיסמת אפליקציה',
    appPasswordHint: '16 אותיות. זו לא הסיסמה הרגילה של החשבון.',
    connect: 'חיבור החשבון',
    connectedTo: 'מחובר. הבקשות נשלחות אל',
    failed: (address: string, when: string) =>
      `Google דחה את הסיסמה של ${address} (${when}), והבקשות לא נשלחות במייל. נתקו את החשבון וחברו אותו מחדש עם סיסמת אפליקציה חדשה.`,
    sendTest: 'שליחת מייל בדיקה',
    testSent: 'מייל הבדיקה נשלח. בדקו את תיבת הדואר.',
    refused: 'Google דחה את סיסמת האפליקציה. נתקו את החשבון וחברו אותו מחדש.',
    unreachable: 'לא הצלחנו להגיע ל-Gmail כרגע. נסו שוב בעוד רגע.',
    notConnected: 'לא מחובר חשבון Gmail.',
    replace: 'החלפת החשבון',
    replaceIntro: 'החשבון החדש יחליף את הקיים רק אחרי ש-Google יאשר אותו.',
    replaced: 'החשבון הוחלף',
    disconnect: 'ניתוק החשבון',
    disconnectTitle: 'לנתק את חשבון ה-Gmail?',
    disconnectBody: 'בקשות חדשות להצעת מחיר לא יישלחו במייל. הן ימשיכו להופיע כאן בניהול החנות.',
  },
  currency: { ILS: 'שקל חדש (₪)' },
  analytics: {
    title: 'אנליטיקס',
    intro:
      'הקודים הרשמיים של Google ו-Meta נטענים בחנות רק כשמוגדר מזהה. מזהה Google Analytics נפתח לחנות אוטומטית; אפשר להחליף אותו במזהה משלכם.',
    ga4: 'מזהה Google Analytics 4',
    ga4Hint: 'למשל G-AB12CD34EF',
    pixel: 'מזהה פיקסל של Meta',
    pixelHint: 'ספרות בלבד',
  },
  staff: {
    title: 'צוות',
    invite: 'הוספת איש צוות',
    email: 'אימייל',
    name: 'שם',
    role: 'תפקיד',
    submit: 'הוספה',
    you: '(את/ה)',
    access: 'כניסה',
    passwordSet: 'הגדיר סיסמה',
    needsPassword: 'טרם הגדיר סיסמה',
    invitationUntil: 'הזמנה פתוחה עד',
    noInvitation: 'אין הזמנה פתוחה',
    newInvitation: 'קישור חדש',
    remove: 'הסרה',
    removeTitle: 'להסיר מצוות החנות?',
    removeBody: 'הגישה לניהול החנות תוסר מיד. החשבון עצמו לא נמחק.',
    inviteTitle: 'קישור הזמנה',
    inviteBody: 'שלחו את הקישור לאיש הצוות. הוא מוצג רק עכשיו, ומאפשר להגדיר סיסמה פעם אחת.',
    existingAccount: 'לאדם הזה כבר יש חשבון. הוא יראה את החנות בכניסה הבאה.',
    joined: 'הצטרף',
  },
  roles: {
    TENANT_OWNER: 'בעלים',
    TENANT_ADMIN: 'מנהל',
    TENANT_STAFF: 'צוות',
  } satisfies Record<TenantRole, string>,
  tenantStatus: {
    PROVISIONING: 'בהקמה',
    ACTIVE: 'פעילה',
    SUSPENDED: 'מושעית',
    CANCELLED: 'בוטלה',
    CLOSED: 'נסגרה',
  } satisfies Record<TenantStatus, string>,
  account: {
    title: 'החשבון שלי',
    password: 'שינוי סיסמה',
    current: 'הסיסמה הנוכחית',
    next: 'סיסמה חדשה (לפחות 10 תווים)',
    confirm: 'אימות הסיסמה החדשה',
    changed: 'הסיסמה שונתה. כל ההתחברויות האחרות נותקו.',
    mfa: 'אימות דו־שלבי',
    mfaIntro:
      'אימות דו־שלבי מגן על החשבון גם אם הסיסמה דלפה. סרקו את הקוד באפליקציית אימות (Google Authenticator, Microsoft Authenticator, 1Password ועוד) והזינו את הקוד שהיא מציגה.',
    mfaOn: 'פעיל',
    mfaOff: 'לא מוגדר',
    start: 'הפעלת אימות דו־שלבי',
    manual: 'אי אפשר לסרוק? הזינו את המפתח ידנית:',
    qrAlt: 'קוד QR להגדרת אפליקציית האימות',
    code: 'הקוד מהאפליקציה',
    enable: 'הפעלה',
    recoveryTitle: 'קודי שחזור',
    recoveryIntro:
      'שמרו את הקודים האלה במקום בטוח. כל קוד משמש פעם אחת, כשאין גישה לאפליקציית האימות. הם לא יוצגו שוב.',
    recoveryDone: 'שמרתי את הקודים',
    remaining: 'קודי שחזור שנותרו:',
    regenerate: 'יצירת קודי שחזור חדשים',
    regenerateHint: 'הזינו קוד מהאפליקציה. הקודים הקודמים יפסיקו לעבוד.',
    disable: 'כיבוי אימות דו־שלבי',
    disableHint: 'לכיבוי, הזינו קוד עדכני מהאפליקציה.',
    regenerateCode: 'קוד לחידוש קודי השחזור',
    disableCode: 'קוד לכיבוי',
  },
  audit: {
    title: 'יומן פעולות',
    when: 'מתי',
    action: 'פעולה',
    actor: 'מבצע',
    resource: 'על מה',
    details: 'פרטים',
    empty: 'אין אירועים להצגה',
    system: 'מערכת',
    anonymous: 'לא מזוהה',
  },
  actions: {
    'tenant.created': 'החנות נוצרה',
    'tenant.suspended': 'החנות הושעתה',
    'tenant.reactivated': 'החנות הופעלה מחדש',
    'tenant.activated': 'ניהול החנות נפתח',
    'tenant.management_entered': 'צוות הפלטפורמה נכנס לניהול החנות',
    'tenant.customer_number_changed': 'החנות שויכה ללקוח של האתר המקורי',
    'tenant.customer_refreshed': 'פרטי הלקוח והמחירים נקראו שוב מהאתר המקורי',
    'provisioning.started': 'הקמת החנות התחילה',
    'provisioning.retry_requested': 'הקמת החנות נשלחה לניסיון חוזר',
    'provisioning.failed': 'הקמת החנות נכשלה',
    'store.opened': 'החנות נפתחה לקונים',
    'store.updated': 'פרטי החנות עודכנו',
    'auth.login_succeeded': 'כניסה',
    'auth.login_failed': 'כניסה נכשלה',
    'auth.account_locked': 'חשבון ננעל',
    'auth.logout': 'יציאה',
    'auth.tenant_switched': 'מעבר בין חנויות',
    'auth.password_changed': 'סיסמה שונתה',
    'auth.password_reset_requested': 'נשלח קישור לאיפוס סיסמה',
    'auth.password_reset': 'סיסמה אופסה דרך קישור במייל',
    'auth.password_set_by_platform': 'צוות הפלטפורמה קבע סיסמה חדשה',
    'auth.mfa_enrolled': 'אימות דו־שלבי הופעל',
    'auth.mfa_verified': 'אימות דו־שלבי עבר',
    'auth.mfa_failed': 'קוד אימות שגוי',
    'auth.mfa_recovery_code_used': 'שימוש בקוד שחזור',
    'auth.mfa_recovery_codes_regenerated': 'קודי שחזור חודשו',
    'auth.mfa_reset': 'אימות דו־שלבי אופס',
    'auth.mfa_disabled': 'אימות דו־שלבי כובה',
    'session.revoked': 'התחברות נותקה',
    'membership.role_granted': 'איש צוות נוסף',
    'membership.role_changed': 'תפקיד של איש צוות שונה',
    'membership.revoked': 'איש צוות הוסר',
    'platform.role_granted': 'צורף לצוות הפלטפורמה',
    'platform.role_changed': 'תפקיד בפלטפורמה שונה',
    'platform.role_revoked': 'הוסר מצוות הפלטפורמה',
    'invitation.issued': 'קישור הזמנה נוצר',
    'invitation.accepted': 'הזמנה מומשה',
    'catalog.ingest_requested': 'התבקש עדכון קטלוג',
    'catalog_sync.requested': 'התבקשה משיכת מוצרים חדשים',
    'catalog_sync.completed': 'משיכת המוצרים הושלמה',
    'catalog_sync.failed': 'משיכת המוצרים נכשלה',
    'product.created': 'מוצר נוסף',
    'product.updated': 'מוצר עודכן',
    'category.created': 'קטגוריה נוספה',
    'category.updated': 'קטגוריה עודכנה',
    'category.deleted': 'קטגוריה נמחקה',
    'product.media_changed': 'תמונות מוצר עודכנו',
    'product.logo_changed': 'לוגו יצרן של מוצר עודכן',
    'product.list_restored': 'רשימה של מוצר הוחזרה לכתיבה אוטומטית',
    'media.uploaded': 'תמונה הועלתה',
    'settings.updated': 'הגדרות החנות עודכנו',
    'theme.updated': 'הלוגו עודכן',
    'home.updated': 'דף הבית עודכן',
    'home_slide.created': 'באנר נוסף לדף הבית',
    'home_slide.updated': 'באנר בדף הבית עודכן',
    'home_slide.deleted': 'באנר נמחק מדף הבית',
    'content_page.created': 'עמוד תוכן נוסף',
    'content_page.updated': 'עמוד תוכן עודכן',
    'content_page.deleted': 'עמוד תוכן נמחק',
    // Shipping zones, coupons and quantity deals are from when stores took orders (adr/0022):
    // nothing writes these now; older entries still show.
    'shipping_zone.created': 'אזור משלוח נוסף',
    'shipping_zone.updated': 'אזור משלוח עודכן',
    'shipping_zone.deleted': 'אזור משלוח נמחק',
    'coupon.created': 'קופון נוסף',
    'coupon.updated': 'קופון עודכן',
    'coupon.deleted': 'קופון נמחק',
    'promotion.created': 'מבצע כמות נוסף',
    'promotion.updated': 'מבצע כמות עודכן',
    'promotion.deleted': 'מבצע כמות נמחק',
    'popup.created': 'חלון קופץ נוסף',
    'popup.updated': 'חלון קופץ עודכן',
    'popup.deleted': 'חלון קופץ נמחק',
    'order.status_changed': 'סטטוס של בקשה להצעת מחיר שונה',
    'order.quote_saved': 'הצעת מחיר נשמרה',
    // Historical too: quote requests have no payment.
    'order.payment_changed': 'מצב תשלום שונה (היסטורי)',
    'customer.status_changed': 'מצב חשבון לקוח שונה',
    'ai_copy.approved': 'תוכן שיווקי אושר',
    'ai_copy.regeneration_requested': 'התבקשה כתיבה מחדש של תוכן שיווקי',
    'ai_copy.retry_requested': 'התבקש ניסיון נוסף לכתיבת תוכן שנכשלה',
    'ai_copy.generation_requested': 'התבקשה כתיבת תוכן למוצרים שחסר להם',
    'ai_copy.flyers_requested': 'התבקשה כתיבת פליירים שיווקיים',
    'ai_copy.settings_updated': 'הגדרת אישור התוכן השיווקי עודכנה',
    'ai_copy.group_edited': 'התוכן השיווקי של קבוצת מוצרים נערך',
    'product_exclusions.updated': 'המוצרים שאינם זמינים לחנות עודכנו',
    'catalog_exposure.updated': 'נתוני הקטלוג המוצגים בחנות עודכנו',
    'ai_budget.updated': 'מכסת כתיבת התוכן עודכנה',
    'domain.added': 'נוספה כתובת לחנות',
    'domain.removed': 'כתובת הוסרה מהחנות',
    'domain.check_requested': 'התבקשה בדיקה של כתובת',
    'domain.certificate_requested': 'התבקשה הנפקת תעודת אבטחה לכתובת',
    'domain.primary_changed': 'הכתובת הראשית של החנות הוחלפה',
    'domain.ownership_verified': 'הבעלות על כתובת אומתה',
    'domain.activated': 'כתובת התחילה לפעול',
    'domain.failed': 'בדיקת כתובת נכשלה',
    'domain.dns_lost': 'כתובת הפסיקה להפנות אל החנות',
    'domain.restored': 'כתובת חזרה לפעול',
    'service_ticket.opened': 'נפתחה תקלה או קריאת שירות',
    'service_ticket.replied': 'נכתבה הודעה בקריאת שירות',
    'service_ticket.status_changed': 'מצב של תקלה או קריאה השתנה',
    'service_ticket.scan_requested': 'סריקת תקלות הופעלה ידנית',
  } satisfies Record<AuditAction, string>,
  errors: {
    VALIDATION_FAILED: 'יש שדות שאינם תקינים',
    BAD_REQUEST: 'הבקשה אינה תקינה',
    UNAUTHENTICATED: 'נדרשת כניסה מחדש',
    SESSION_EXPIRED: 'פג תוקף ההתחברות. היכנסו שוב.',
    MFA_REQUIRED: 'נדרש קוד אימות',
    FORBIDDEN: 'אין הרשאה לפעולה הזו',
    MFA_ENROLLMENT_REQUIRED: 'יש להגדיר אימות דו־שלבי',
    TENANT_SUSPENDED: 'החנות מושעית. אפשר רק לצפות בנתונים.',
    NOT_FOUND: 'לא נמצא',
    METHOD_NOT_ALLOWED: 'הפעולה אינה נתמכת',
    CONFLICT:
      'מישהו אחר שינה את הנתונים בינתיים, או שהפעולה מתנגשת במצב הנוכחי. טענו את הדף מחדש ונסו שוב.',
    PAYLOAD_TOO_LARGE: 'הקובץ גדול מדי (עד 5MB)',
    UNSUPPORTED_MEDIA_TYPE: 'סוג הקובץ אינו נתמך. אפשר להעלות תמונות בלבד.',
    RATE_LIMITED: 'יותר מדי ניסיונות. המתינו מעט ונסו שוב.',
    INTERNAL: 'אירעה תקלה בשרת. נסו שוב מאוחר יותר.',
    SERVICE_UNAVAILABLE: 'השרת אינו זמין כרגע. בדקו את החיבור ונסו שוב.',
    STORE_NOT_OPEN: 'החנות עדיין לא פתוחה לקונים',
    STORE_CLOSED: 'החנות סגורה זמנית לקונים',
    ORDERING_CLOSED: 'החנות אינה מקבלת בקשות להצעת מחיר כרגע',
    CART_INVALID: 'חלק מהמוצרים בסל השתנו',
  } satisfies Record<ErrorCode, string>,
  serverMessages,
  validation,
};

/** Fills `{name}` placeholders of a dictionary string. */
export function fill(text: string, values: Readonly<Record<string, string | number>>): string {
  return text.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in values ? String(values[key]) : whole,
  );
}
